import { AttributeMap } from 'aws-sdk/clients/dynamodb'
import { createBatches, getList } from './utils'
import { KeyParams, ListResponse, RangeParams } from './types'
import { DynamoApi } from './dynamo'

export const useDynamoActions = () => {
    return (dynamoApi: DynamoApi, tKeyValue?: string) => {
        const getAll = async (
            dbTableName: string,
            propName?: string,
        ) => {
            let lastKey
            const list: AttributeMap[] = []
            do {
                const response: ListResponse = await getBatch(dbTableName, lastKey, undefined, propName)
                list.push(...response.Items)
                lastKey = response.LastEvaluatedKey
            } while (lastKey)
            return list
        }

        const getBatch = (
            dbTableName: string,
            startKey?: KeyParams,
            limit?: number,
            propName?: string,
        ) => {
            return dynamoApi.getAll(dbTableName, tKeyValue, startKey, limit, propName)
        }

        const get = (
            dbTableName: string,
            uKeyValue: string
        ) => {
            return dynamoApi.get(dbTableName, uKeyValue, tKeyValue)
        }

        const getMultiple = async (
            dbTableName: string,
            uKeyValues: string[]
        ) => {
            const batches = createBatches<string>(uKeyValues, 100)
            const promises = batches.map(batch => dynamoApi.getMultiple(dbTableName, batch, tKeyValue))
            return Promise.all(promises).then(results => results.flat())
        }

        const getFiltered = async (
            dbTableName: string,
            paramsFilter?: KeyParams,
            rangeFilters?: RangeParams,
            existingParamsFilter?: string[]
        ) => {
            let lastKey
            const list: AttributeMap[] = []
            do {
                const response: ListResponse = await getBatchFiltered(dbTableName, lastKey, undefined, paramsFilter, rangeFilters, existingParamsFilter)
                list.push(...response.Items)
                lastKey = response.LastEvaluatedKey
            } while (lastKey)
            return list
        }

        const getBatchFiltered = (
            dbTableName: string,
            startKey?: KeyParams,
            limit?: number,
            paramsFilter?: KeyParams,
            rangeFilters?: RangeParams,
            existingParamsFilter?: string[]
        ) => {
            return dynamoApi.getFiltered(dbTableName, tKeyValue!, startKey, limit, paramsFilter, rangeFilters, existingParamsFilter)
        }

        // TODO unificar getFiltered para que reciba cualquier tipo de filtro
        const getFilteredByList = async (
            dbTableName: string,
            key: string,
            values: string[]
        ): Promise<AttributeMap[]> => {
            const batches = createBatches<string>(values, 100)

            const promises = batches.map(async batch => {
                let lastKey
                const list: AttributeMap[] = []
                do {
                    const response: ListResponse = await getBatchFilteredByList(dbTableName, key, batch, lastKey)
                    list.push(...response.Items)
                    lastKey = response.LastEvaluatedKey
                } while (lastKey)
                return list
            })

            return Promise.all(promises).then(list => list.flat())
        }

        const getBatchFilteredByList = (
            dbTableName: string,
            key: string,
            values: string[],
            startKey?: KeyParams
        ) => {
            const paramsFilter = { [key]: values }
            return dynamoApi.getFiltered(dbTableName, tKeyValue!, startKey, undefined, paramsFilter)
        }

        const count = (
            dbTableName: string,
            startKey?: KeyParams,
            limit?: number,
            paramsFilter?: KeyParams
        ) => {
            return dynamoApi.count(dbTableName, tKeyValue!, startKey, limit, paramsFilter)
        }

        const createOrUpdate = async (
            dbTableName: string,
            list: AttributeMap[]
        ) => {
            const batches = createBatches<AttributeMap>(list)
            if (batches.length === 1) {
                return dynamoApi.createOrUpdate(dbTableName, list)
                    .then(unprocessedItems => getList(dbTableName, unprocessedItems))
            } else {
                const promises = batches.map(batch => dynamoApi.createOrUpdate(dbTableName, batch))
                return Promise.all(promises).then(unprocessedItemsList => {
                    const unprocessedItems = unprocessedItemsList.reduce((items, unprocessedItemsItem) => 
                        ({
                            ...items,
                            ...unprocessedItemsItem
                        })
                    , {})
                    return getList(dbTableName, unprocessedItems)
                })
            }
        }

        const remove = async (
            dbTableName: string,
            uKeyValues: string[]
        ) => {
            const batches = createBatches<string>(uKeyValues)
            if (batches.length === 1) {
                return dynamoApi.remove(dbTableName, uKeyValues, tKeyValue)
                    .then(unprocessedItems => getList(dbTableName, unprocessedItems))
            } else {
                const promises = batches.map(batch => dynamoApi.remove(dbTableName, batch, tKeyValue))
                return Promise.all(promises).then(unprocessedItemsList => {
                    const unprocessedItems = unprocessedItemsList.reduce((items, unprocessedItemsItem) => 
                        ({
                            ...items,
                            ...unprocessedItemsItem
                        })
                    , {})
                    return getList(dbTableName, unprocessedItems)
                })
            }
        }

        return {
            getAll,
            getBatch,
            get,
            getMultiple,
            getFiltered,
            getFilteredByList,
            count,
            createOrUpdate,
            remove
        }
    }
}
